import { Data, Modal, Tags as TagsModule, UI } from 'common';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import uniqueId from 'lodash/uniqueId';
import React from 'react';
import ManageCategoriesModal from './ManageCategoriesModal.component';
import ProductAlias from './ProductAlias.component';
import ProductDetailsHeader from './ProductDetailsHeader.component';

const {
	DimensionsFieldWithLabel,
	WeightFieldWithLabel,
	WrappedAccordion,
	WrappedAccordionTabTemplate,
	WrappedButton,
	WrappedButtonType,
	WrappedCheckbox,
	WrappedDropdownWithLabel,
	WrappedInputNumberWithLabel,
	WrappedInputTextWithLabel,
	WrappedInputTextareaWithLabel,
	WrappedMultiSelectWithLabel,
} = UI;
const { Countries } = Data;
const { ModalContext } = Modal;
const { ManageTagsModal } = TagsModule;

// constant
const SelectOption = {
	ManageCategory: 'ManageCategory',
	ManageTag: 'ManageTag',
};

class SingleProductEditView extends React.Component {
	constructor(props) {
		super(props);

		const { product = {} } = props;

		this.state = {
			accordionActiveIndex: [0, 1, 2], // default to expand all three panels
			validations: {
				// 0 = untouched, -1 = failed, 1 = valid
				SKU: product.SKU !== '' ? 1 : 0,
				Name: product.Name !== '' ? 1 : 0,
				ProductAliases: product.ProductAliases.every(
					({ AliasSKU }) => AliasSKU !== ''
				)
					? 1
					: 0,
			},
			editingProduct: { ...product },
		};
	}

	componentDidUpdate(prevProps) {
		const { product } = this.props;

		// refresh Product data
		if (!isEqual(prevProps.product, product)) {
			this.refreshProductData(product);
		}
	}

	// get the tag options which have not been selected
	getTagOptions = () => {
		const { tagOptions } = this.props;
		const {
			editingProduct: { Tags },
		} = this.state;
		const options = tagOptions
			.map(tag => ({
				...tag,
				tagColor: tag.Color,
				name: tag.Tag,
			}))
			.filter(tag => !Tags.includes(tag.OrderTagID));

		return [
			...options,
			{
				name: 'Manage Tags...',
				value: '',
				OrderTagID: SelectOption.ManageTag,
			},
		];
	};

	validateProductBeforeSaving = () => {
		const { saveProductCallback, product } = this.props;
		const { editingProduct, validations } = this.state;
		const isValid =
			!Object.values(validations).includes(-1) &&
			editingProduct.SKU !== '' &&
			editingProduct.Name !== '';

		// only save when there's new changes and validation passes
		if (
			!isEqual(product, editingProduct) &&
			isValid &&
			isFunction(saveProductCallback)
		) {
			saveProductCallback(editingProduct);
		}
	};

	// refresh editingProduct data if product data has changed
	refreshProductData = product => {
		this.setState({
			editingProduct: { ...product },
		});
	};

	// custom selected tag template in MultiSelect component
	selectedTagTemplate = orderTagId => {
		const { tagOptions } = this.props;
		const tag =
			tagOptions.find(option => option.OrderTagID === orderTagId) || null;

		return tag ? (
			<span
				key={orderTagId}
				value={orderTagId}
				className="multiselect-selected-item-template"
			>
				<span className="tag-color" style={{ background: tag.Color }} />
				<span className="name">{tag.Tag}</span>
				<i
					aria-label="Remove Tag"
					id={orderTagId}
					role="button"
					tabIndex={0}
					className="remove pi pi-times"
					onClick={this.onClickRemoveTag}
				/>
			</span>
		) : null;
	};

	// On field blur, validate and save input value
	onBlur = ({ target: { name } }) => {
		// validate input data and save is validation passes
		this.setState(({ editingProduct, validations }) => {
			if (name === 'Name' || name === 'SKU') {
				return {
					validations: {
						...validations,
						[name]: editingProduct[name] !== '' ? 1 : -1,
					},
				};
			}
			// AliasSKU field in the productAliases array should not be empty
			else if (name.includes('AliasSKU')) {
				return {
					validations: {
						...validations,
						ProductAliases: editingProduct.ProductAliases.some(
							({ AliasSKU }) => AliasSKU === ''
						)
							? -1
							: 1,
					},
				};
			}
			return null;
		}, this.validateProductBeforeSaving);
	};

	onClickAddAlias = () => {
		const { editingProduct, validations } = this.state;
		const { ProductID, ProductAliases } = editingProduct;

		this.setState({
			editingProduct: {
				...editingProduct,
				ProductAliases: [
					...ProductAliases,
					{
						id: uniqueId(),
						AliasSKU: '',
						ProductID,
						ProductAliasID: null,
						StoreID: null,
					},
				],
			},
			validations: {
				...validations,
				// remain
				ProductAliases: ProductAliases.some(({ AliasSKU }) => AliasSKU === '')
					? -1
					: 0,
			},
		});
	};

	onClickRemoveAlias = ({ currentTarget: { name } }) => {
		const { editingProduct, validations } = this.state;
		const { ProductAliases } = editingProduct;
		const newProductAliases = ProductAliases.filter(alias => alias.id !== name);

		this.setState(
			{
				editingProduct: {
					...editingProduct,
					ProductAliases: newProductAliases,
				},
				validations: {
					...validations,
					ProductAliases: newProductAliases.some(
						({ AliasSKU }) => AliasSKU === ''
					)
						? -1
						: 1,
				},
			},
			this.validateProductBeforeSaving
		);
	};

	// remove the selected tag
	onClickRemoveTag = e => {
		const {
			target: { id },
		} = e;

		e.stopPropagation();

		this.setState(({ editingProduct }) => {
			const tags = editingProduct.Tags.filter(tagId => tagId !== +id);

			return {
				editingProduct: {
					...editingProduct,
					Tags: tags,
					ProductTags: tags.map(tagId => ({
						ProductId: editingProduct.ProductID,
						OrderTagID: tagId,
					})),
				},
			};
		}, this.validateProductBeforeSaving);
	};

	onClickCheckbox = ({ target: { checked, name } }) => {
		this.setState(
			({ editingProduct }) => ({
				editingProduct: {
					...editingProduct,
					[name]: checked,
				},
			}),
			this.validateProductBeforeSaving
		);
	};

	onChangeAlias = ({ target: { name, value } }) => {
		const [fieldName, aliasId] = name.split('-');
		const { editingProduct } = this.state;
		const changedAlias = editingProduct.ProductAliases.find(
			alias => alias.id === aliasId
		);

		this.setState(
			{
				editingProduct: {
					...editingProduct,
					ProductAliases: editingProduct.ProductAliases.map(alias =>
						alias === changedAlias
							? { ...changedAlias, [fieldName]: value }
							: alias
					),
				},
			},
			() => {
				// save when the user selects a different store
				if (fieldName === 'StoreID') {
					this.validateProductBeforeSaving();
				}
			}
		);
	};

	onChangeSelect = ({ target: { name, value } }) => {
		const { showModal } = this.context;
		const {
			categoryOptions,
			refreshCategoryOptionsCallback,
			refreshProductsCallback,
			onUpdateTagOptionsCallback,
		} = this.props;

		if (value === SelectOption.ManageCategory) {
			showModal({
				component: ManageCategoriesModal,
				data: {
					categoryOptions,
					refreshCategoryOptionsCallback,
					refreshProductsCallback,
				},
			});
		}
		// value from MultiSelect is array type
		else if (isArray(value) && value.includes(SelectOption.ManageTag)) {
			showModal({
				component: ManageTagsModal,
				data: {
					onUpdateTagOptionsCallback,
				},
			});
		} else {
			this.setState(
				({ editingProduct }) => ({
					editingProduct: {
						...editingProduct,
						ProductTags:
							name === 'Tags'
								? value.map(tagId => ({
										ProductID: editingProduct.ProductID,
										OrderTagID: tagId,
									}))
								: editingProduct.ProductTags,
						[name]: value,
					},
				}),
				this.validateProductBeforeSaving
			);
		}
	};

	onChangeWeight = ({ pounds, ounces }) => {
		this.setState(
			({ editingProduct }) => ({
				editingProduct: {
					...editingProduct,
					Weight: pounds * 16 + ounces,
				},
			}),
			this.validateProductBeforeSaving
		);
	};

	onChangeDimension = ({ length, width, height }) => {
		this.setState(
			({ editingProduct }) => ({
				editingProduct: {
					...editingProduct,
					Width: width,
					Length: length,
					Height: height,
				},
			}),
			this.validateProductBeforeSaving
		);
	};

	onChangeText = ({ target: { name, value } }) => {
		this.setState(({ editingProduct }) => ({
			editingProduct: {
				...editingProduct,
				[name]: value,
			},
		}));
	};

	renderSkuGroup = () => {
		const { categoryOptions = [] } = this.props;
		const {
			editingProduct: {
				CategoryId,
				InternalNotes,
				Name,
				SKU,
				Tags,
				ThumbnailUrl,
				ProductID,
				UPC,
				UseProductName,
			},
			validations,
		} = this.state;

		return (
			<div className="sku-group">
				<WrappedInputTextWithLabel
					label="SKU:"
					errorMessage="SKU is required."
					isInputInvalid={validations.SKU === -1}
					uiElementConfig={{
						name: 'SKU',
						value: SKU,
						disabled: ProductID !== -1,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-sku',
					}}
				/>
				<WrappedInputTextWithLabel
					label="Name:"
					errorMessage="Name is required."
					isInputInvalid={validations.Name === -1}
					uiElementConfig={{
						name: 'Name',
						value: Name,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-name',
					}}
				/>
				<WrappedCheckbox
					name="UseProductName"
					checked={UseProductName}
					onChange={this.onClickCheckbox}
					dataqa="product-edit-use-name"
				>
					Override imported product name
				</WrappedCheckbox>
				<WrappedInputTextWithLabel
					label="Description:"
					uiElementConfig={{
						name: 'InternalNotes',
						value: InternalNotes,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-description',
					}}
				/>
				<WrappedInputTextWithLabel
					label="Override Image:"
					uiElementConfig={{
						name: 'ThumbnailUrl',
						value: ThumbnailUrl,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-thumbnail-url',
					}}
				/>
				<WrappedDropdownWithLabel
					label="Category:"
					uiElementConfig={{
						dataqa: 'select-product-category',
						dataKey: 'CategoryID',
						name: 'CategoryId',
						options: [
							{
								CategoryID: SelectOption.ManageCategory,
								Name: 'Add/Edit Category',
							},
							...categoryOptions,
							{
								CategoryID: '',
								Name: 'None',
							},
						],
						optionLabel: 'Name',
						optionValue: 'CategoryID',
						value: CategoryId,
						onChange: this.onChangeSelect,
					}}
				/>
				<WrappedMultiSelectWithLabel
					label="Tags:"
					uiElementConfig={{
						dataqa: 'product-tag',
						maxSelectedLabels: 10,
						name: 'Tags',
						options: this.getTagOptions(),
						optionLabel: 'Tag',
						optionValue: 'OrderTagID',
						placeholder: 'Select tags...',
						selectedItemTemplate: this.selectedTagTemplate,
						value: Tags,
						onChange: this.onChangeSelect,
					}}
				/>
				<WrappedInputTextWithLabel
					label="UPC:"
					uiElementConfig={{
						name: 'UPC',
						value: UPC,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-upc',
					}}
				/>
			</div>
		);
	};

	renderShippingDefaultGroup = () => {
		const {
			editingProduct: { Height, Length, WarehouseLocation, Width, Weight },
		} = this.state;

		return (
			<div className="shipping-defaults-group">
				<WeightFieldWithLabel
					label="Weight:"
					uiElementConfig={{
						onChange: this.onChangeWeight,
						weight: { pounds: Math.floor(Weight / 16), ounces: Weight % 16 },
						doValidation: false,
					}}
				/>
				<DimensionsFieldWithLabel
					label="Dimensions:"
					uiElementConfig={{
						onChange: this.onChangeDimension,
						dimensions: { length: Length, width: Width, height: Height },
						doValidation: false,
					}}
				/>
				<WrappedInputTextWithLabel
					label="Warehouse Location:"
					uiElementConfig={{
						name: 'WarehouseLocation',
						value: WarehouseLocation,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-warehouse-location',
					}}
				/>
			</div>
		);
	};

	renderCustomOverrideGroup = () => {
		const {
			editingProduct: {
				CustomsCountry,
				CustomsDescription,
				CustomsTariffNo,
				CustomsValue,
				NoCustoms,
			},
		} = this.state;

		const isDisabled = NoCustoms === true;
		const disabledCls = isDisabled ? 'disabled' : '';

		return (
			<div className="custom-override-group">
				<p>
					These options will be used to create customs declarations for
					international shipments that contain this product.
				</p>
				<WrappedCheckbox
					name="NoCustoms"
					checked={NoCustoms}
					onChange={this.onClickCheckbox}
					dataqa="product-edit-no-customs"
				>
					Don't declare this product on customs form
				</WrappedCheckbox>
				<WrappedInputTextareaWithLabel
					className={disabledCls}
					label="Description:"
					uiElementConfig={{
						disabled: isDisabled,
						autoResize: true,
						name: 'CustomsDescription',
						value: CustomsDescription,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-customs-decription',
					}}
				/>
				<WrappedInputNumberWithLabel
					className={disabledCls}
					label="Declared Value $:"
					uiElementConfig={{
						disabled: isDisabled,
						name: 'CustomsValue',
						value: CustomsValue,
						incrementButtonIcon: 'pi pi-chevron-up',
						decrementButtonIcon: 'pi pi-chevron-down',
						showButtons: true,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-customs-value',
					}}
				/>
				<WrappedDropdownWithLabel
					className={disabledCls}
					label="Country of Origin:"
					uiElementConfig={{
						dataqa: 'select-custom-override-country-origin',
						disabled: isDisabled,
						name: 'CustomsCountry',
						options: Countries,
						optionLabel: 'countryName',
						optionValue: 'countryCode',
						value: CustomsCountry,
						onChange: this.onChangeSelect,
					}}
				/>
				<WrappedInputTextWithLabel
					className={disabledCls}
					label="HS Tariff:"
					uiElementConfig={{
						disabled: isDisabled,
						name: 'CustomsTariffNo',
						value: CustomsTariffNo,
						onChange: this.onChangeText,
						onBlur: this.onBlur,
						dataqa: 'product-edit-customs-tariff-number',
					}}
				/>
			</div>
		);
	};

	renderStoreAliasGroup = () => {
		const { storeStatus } = this.props;
		const {
			editingProduct: { ProductAliases },
		} = this.state;

		return (
			<div className="store-alias-group">
				<p>
					Store aliases will map future orders to this product for product
					details and reporting purposes.
				</p>
				{ProductAliases.map(alias => (
					<ProductAlias
						key={alias.id}
						alias={alias}
						storeStatus={storeStatus}
						onChangeAlias={this.onChangeAlias}
						onClickAddAlias={this.onClickAddAlias}
						onClickRemoveAlias={this.onClickRemoveAlias}
						onBlur={this.onBlur}
					/>
				))}
				<WrappedButton
					className="add-store-alias"
					buttonType={WrappedButtonType.TertiaryGrey}
					icon="add-icon"
					iconPos="left"
					label="Add Store Alias"
					onClick={this.onClickAddAlias}
					dataqa="product-edit-add-store-alias"
				/>
			</div>
		);
	};

	render() {
		const {
			accordionActiveIndex,
			editingProduct: { ProductID, SKU },
		} = this.state;
		const { onDeleteProductCallback, collapseEastPanel } = this.props;

		return (
			<div className="products-single-product-edit-view">
				<ProductDetailsHeader
					headerText={ProductID === -1 ? 'Product Details' : SKU}
					productCount={1}
					onDeleteProductCallback={onDeleteProductCallback}
					collapseEastPanel={collapseEastPanel}
				/>
				<div className="form-container">
					{/* SKU panel */}
					{this.renderSkuGroup()}
					{/* Shipping, Custom Override and Store Aliases */}
					<WrappedAccordion
						activeIndex={accordionActiveIndex}
						className="accordion-tab"
						multiple
						dataqa="product-edit-product-details"
					>
						<WrappedAccordionTabTemplate>
							<div className="accordion-header">
								<span className="header-text">Shipping Defaults</span>
							</div>
							{this.renderShippingDefaultGroup()}
						</WrappedAccordionTabTemplate>
						<WrappedAccordionTabTemplate>
							<div className="accordion-header">
								<span className="header-text">Custom Override</span>
							</div>
							{this.renderCustomOverrideGroup()}
						</WrappedAccordionTabTemplate>
						<WrappedAccordionTabTemplate>
							<div className="accordion-header">
								<span className="header-text">Store Aliases</span>
							</div>
							{this.renderStoreAliasGroup()}
						</WrappedAccordionTabTemplate>
					</WrappedAccordion>
				</div>
			</div>
		);
	}
}

SingleProductEditView.contextType = ModalContext;

export default SingleProductEditView;
